import i18next from 'i18next';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation as useReactI18nextTranslation } from 'react-i18next';
import { ensureNamespaces } from 'shared/i18n';

/**
 * Custom hook that wraps react-i18next's useTranslation
 * It ensures any requested namespaces are loaded before rendering to avoid flicker.
 * Returns loading state to allow components to handle the loading gracefully.
 */
export function useTranslation(namespace?: string | string[]) {
  // Create a stable array for dependency tracking regardless of array identity
  const namespacesArray = useMemo(() =>
    Array.isArray(namespace)
      ? namespace
      : namespace
      ? [namespace]
      : [],
    [namespace]
  );
  const namespaceKey = namespacesArray.join('|');

  // Track current language to re-check namespaces when language changes
  const [currentLang, setCurrentLang] = useState(i18next.language);

  const [isReady, setIsReady] = useState(() => {
    if (namespacesArray.length === 0) return true;
    return namespacesArray.every((ns) => i18next.hasResourceBundle(i18next.language, ns));
  });

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChanged = (lang: string) => {
      setCurrentLang(lang);
    };
    i18next.on('languageChanged', handleLanguageChanged);
    return () => {
      i18next.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  // Load namespaces when language or namespaces change
  useEffect(() => {
    if (namespacesArray.length === 0) {
      if (!isReady) setIsReady(true);
      return;
    }

    const loadNamespaces = async () => {
      try {
        await ensureNamespaces(namespacesArray, currentLang);
        setIsReady(true);
      } catch (error) {
        console.error('Failed to load translation namespaces:', error);
        setIsReady(true); // Allow rendering even if loading fails
      }
    };

    const allLoaded = namespacesArray.every((ns) => i18next.hasResourceBundle(currentLang, ns));

    if (allLoaded) {
      if (!isReady) setIsReady(true);
    } else {
      setIsReady(false);
      void loadNamespaces();
    }
  }, [namespaceKey, namespacesArray, currentLang, isReady]);

  // Forward a stable parameter to react-i18next to avoid re-runs on array literal identity changes
  const nsParam: string | string[] | undefined = namespacesArray.length > 0 ? namespacesArray : undefined;
  const translationResult = useReactI18nextTranslation(nsParam as any);

  return {
    ...translationResult,
    ready: isReady,
  };
}

/**
 * Hook for accessing translations from multiple namespaces
 * Usage: const { t } = useNamespacedTranslation(['common', 'patients']);
 */
export function useNamespacedTranslation(namespaces: string[]) {
  const namespacesArray = useMemo(() => namespaces ?? [], [namespaces]);
  const namespaceKey = namespacesArray.join('|');

  // Track current language to re-check namespaces when language changes
  const [currentLang, setCurrentLang] = useState(i18next.language);

  const [isReady, setIsReady] = useState(() => {
    if (namespacesArray.length === 0) return true;
    return namespacesArray.every((ns) => i18next.hasResourceBundle(i18next.language, ns));
  });

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChanged = (lang: string) => {
      setCurrentLang(lang);
    };
    i18next.on('languageChanged', handleLanguageChanged);
    return () => {
      i18next.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  // Load namespaces when language or namespaces change
  useEffect(() => {
    if (namespacesArray.length === 0) {
      if (!isReady) setIsReady(true);
      return;
    }

    const loadNamespaces = async () => {
      try {
        await ensureNamespaces(namespacesArray, currentLang);
        setIsReady(true);
      } catch (error) {
        console.error('Failed to load translation namespaces:', error);
        setIsReady(true); // Allow rendering even if loading fails
      }
    };

    const allLoaded = namespacesArray.every((ns) => i18next.hasResourceBundle(currentLang, ns));

    if (allLoaded) {
      if (!isReady) setIsReady(true);
    } else {
      setIsReady(false);
      void loadNamespaces();
    }
  }, [namespaceKey, namespacesArray, currentLang, isReady]);

  // Pass a stable array instance to react-i18next
  const translationResult = useReactI18nextTranslation(namespacesArray as any);

  return {
    ...translationResult,
    ready: isReady,
  };
}
